// ADR 28.9.26e §8 — the nav hides where Android hides it.
//
// Android's `HIDDEN_ROUTES` + `shouldShowTabBar` (`TopLevelTab.kt`) give
// `NavigationSuiteType.None` on review, the lesson walkthrough, auth and
// verify. This is the same set spelt in the web's own route vocabulary:
//
//   /flashcards/review   ← flashcards/review
//   /plans/:id/play      ← lesson-plans/{id}/play
//   /signin, /register   ← auth
//   /verify              ← verify
//
// Android's `subscription` and `…/exercise` have no web route (the web's
// exercise is inline set cards, 16.9.26o). Platform code, not rust-core: the
// input is each platform's own route vocabulary, and a shared table would be a
// translation layer with one row in common.
//
// Only the **nav** hides. The header stays, because Android's per-screen
// `TopAppBar` does too — and an immersive screen's way out is the header's
// back arrow (`backTo` on the route's handle, §7).
//
// No size-class string is spelt here: `navChrome` is compared to `'rail'`, a
// nav-chrome value, which keeps this file outside 6.9.26p AC6's exempt pair.

import { useLocation } from 'react-router-dom'

import type { NavChrome } from '../wasm'

const IMMERSIVE: RegExp[] = [
  /^\/flashcards\/review\/?$/,
  /^\/plans\/[^/]+\/play\/?$/,
  /^\/signin\/?$/,
  /^\/register\/?$/,
  /^\/verify\/?$/,
]

/** True where Android shows no navigation suite. `pathname` carries no query. */
export function isImmersive(pathname: string): boolean {
  const path = pathname.split(/[?#]/)[0]
  return IMMERSIVE.some((pattern) => pattern.test(path))
}

/** Whether the chrome is a rail, and whether any nav shows at all on this route. */
export function useNavVisibility(navChrome: NavChrome): { rail: boolean; showNav: boolean } {
  const { pathname } = useLocation()
  return { rail: navChrome === 'rail', showNav: !isImmersive(pathname) }
}
